import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Building2, Phone, User, Shield, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import LanguageSelector from '../components/LanguageSelector';

const EmployeeLogin = () => {
  const navigate = useNavigate();
  const { login, user, isAdmin } = useAuth();
  const [stage, setStage] = useState('credentials'); // credentials, otp
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ employee_id: '', mobile: '' });
  const [otp, setOtp] = useState('');
  const [requestId, setRequestId] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const [testOtp, setTestOtp] = useState('');

  useEffect(() => {
    // Redirect if already logged in
    if (user) {
      if (isAdmin) {
        navigate('/admin');
      } else {
        navigate('/survey');
      }
    }
  }, [user, isAdmin, navigate]);

  useEffect(() => {
    let interval;
    if (resendCountdown > 0) {
      interval = setInterval(() => setResendCountdown(prev => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendCountdown]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!formData.employee_id || !formData.mobile) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.sendOTP(formData);
      setRequestId(response.data.request_id);
      setTestOtp(response.data.otp_for_testing || '');
      setStage('otp');
      setResendCountdown(30);
      toast.success('OTP sent to your registered mobile number');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter complete OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.verifyOTP({
        employee_id: formData.employee_id,
        mobile: formData.mobile,
        otp
      });
      login(response.data);
      toast.success('Welcome! Redirecting to survey...');
      navigate('/survey');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      const response = await authAPI.sendOTP(formData);
      setRequestId(response.data.request_id);
      setTestOtp(response.data.otp_for_testing || '');
      setResendCountdown(30);
      setOtp('');
      toast.success('OTP resent successfully');
    } catch (error) {
      toast.error('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Hero Section */}
      <div 
        className="lg:w-1/2 min-h-[50vh] lg:min-h-screen relative bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(/hotel-lounge.jpg)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-primary/60 to-transparent"></div>
        <div className="absolute inset-0 flex flex-col justify-center p-4 sm:p-8 lg:p-16">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 sm:p-8 lg:p-10 max-w-md mx-auto lg:mx-0 shadow-2xl animate-fade-in">
            <div className="mb-4 sm:mb-6">
              <img src="/gnosis-logo.png" alt="Gnosis Hospitality" className="w-48 sm:w-56 lg:w-72 h-auto object-contain mx-auto lg:mx-0" />
            </div>
            <h1 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold text-primary mb-2 sm:mb-3 text-center lg:text-left">
              Employee Voice Matters
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base text-center lg:text-left">
              Your feedback helps us create a better workplace. All responses are kept confidential.
            </p>
            <div className="mt-4 sm:mt-6 flex items-center gap-3 justify-center lg:justify-start">
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-secondary/20 flex items-center justify-center">
                <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-secondary" />
              </div>
              <div>
                <p className="text-xs sm:text-sm font-semibold text-primary">100% Anonymous</p>
                <p className="text-xs text-muted-foreground">Your identity is protected</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Login Form Section */}
      <div className="lg:w-1/2 flex flex-col items-center justify-center p-4 sm:p-6 lg:p-12 bg-card min-h-[50vh] lg:min-h-screen relative">
        <div className="absolute top-4 right-4 z-10">
          <LanguageSelector />
        </div>
        <Card className="w-full max-w-md border-0 shadow-[0_8px_30px_rgb(0,0,0,0.04)] animate-scale-in">
          <CardHeader className="space-y-1 pb-4 sm:pb-6">
            <CardTitle className="font-heading text-xl sm:text-2xl text-center">
              {stage === 'credentials' ? 'Employee Survey Portal' : 'Verify OTP'}
            </CardTitle>
            <CardDescription className="text-center text-sm">
              {stage === 'credentials' 
                ? 'Enter your credentials to access the motivation survey'
                : `Enter the 6-digit OTP sent to ${formData.mobile.slice(0, 2)}****${formData.mobile.slice(-4)}`
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            {stage === 'credentials' ? (
              <form onSubmit={handleSendOTP} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="employee_id" className="text-sm font-medium">
                    Employee ID
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="employee_id"
                      data-testid="employee-id-input"
                      placeholder="Enter Employee ID"
                      value={formData.employee_id}
                      onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                      className="pl-10 h-12"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mobile" className="text-sm font-medium">
                    Registered Mobile Number
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="mobile"
                      data-testid="mobile-input"
                      placeholder="Enter Mobile Number"
                      value={formData.mobile}
                      onChange={(e) => setFormData({ ...formData, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                      className="pl-10 h-12"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  data-testid="send-otp-btn"
                  className="w-full h-12 bg-primary hover:bg-primary/90 rounded-full font-semibold"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Shield className="h-4 w-4 mr-2" />
                  )}
                  {loading ? 'Sending OTP...' : 'Send OTP'}
                </Button>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="flex flex-col items-center space-y-4">
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    onChange={setOtp}
                    data-testid="otp-input"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                  
                  {testOtp && (
                    <p className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
                      Test OTP: <span className="font-mono font-bold">{testOtp}</span>
                    </p>
                  )}
                </div>

                <Button 
                  onClick={handleVerifyOTP}
                  data-testid="verify-otp-btn"
                  className="w-full h-12 bg-primary hover:bg-primary/90 rounded-full font-semibold"
                  disabled={loading || otp.length !== 6}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <ArrowRight className="h-4 w-4 mr-2" />
                  )}
                  {loading ? 'Verifying...' : 'Verify & Continue'}
                </Button>

                <div className="flex flex-col items-center gap-2">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={resendCountdown > 0 || loading}
                    className="text-sm text-secondary hover:text-secondary/80 disabled:text-muted-foreground transition-colors"
                  >
                    {resendCountdown > 0 ? `Resend OTP in ${resendCountdown}s` : 'Resend OTP'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStage('credentials');
                      setOtp('');
                      setTestOtp('');
                    }}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Change credentials
                  </button>
                </div>
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-border">
              <p className="text-xs text-center text-muted-foreground">
                <Shield className="inline h-3 w-3 mr-1" />
                Your responses are confidential and will be used to improve workplace satisfaction.
              </p>
            </div>

            {/* Admin Link */}
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate('/admin/login')}
                data-testid="admin-login-link"
                className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors"
              >
                Admin Login
              </button>
            </div>
          </CardContent>
        </Card>
        <div className="mt-4 text-center" data-testid="global-footer">
          <p className="text-xs text-muted-foreground/70">Powered by HotelPulse.in | All Rights Reserved &copy; 2026</p>
          <p className="text-xs text-muted-foreground/50 mt-0.5">For support, connect at info@hotelpulse.in</p>
        </div>
      </div>
    </div>
  );
};

export default EmployeeLogin;
